import { pathOr } from 'ramda'
import { getCareCategoryIcon } from 'sidecar-utilities'

/**
 * Determines what fields to use when calculating the prices for a given care.
 * Most of the logic is on the backend so we only need to choose the one based
 * on the users' receivedAt selection, and whether to use the addOn price or the
 * regular price.
 *
 * @param {str} receiveAt Where the service is being performed
 * @param {obj} careDetails Specifically, the price information
 */
export const getCareCosts = (receiveAt = '', careDetails = {}) => {
  const isEpisode = !!careDetails.episode
  const isFacility = /(surgical|facility)/i.test(receiveAt)

  // The same rate model is available in each of the careDetails.rates entries,
  // so if the careDetail is an episode of care, use the latest year
  const determinedCareDetails = isEpisode
    ? careDetails.rates[new Date().getFullYear()]
    : careDetails

  const {
    facilityShouldCost = 0,
    facilityBenefitAmount = 0,
    facilitySidecarCovers = 0,
    facilityAddOnShouldCost = 0,
    facilityAddOnBenefitAmount = 0,
    facilityAddOnSidecarCovers = 0,
    nonFacilityShouldCost = 0,
    nonFacilityBenefitAmount = 0,
    nonFacilitySidecarCovers = 0,
    nonFacilityAddOnShouldCost = 0,
    nonFacilityAddOnBenefitAmount = 0,
    nonFacilityAddOnSidecarCovers = 0,
  } = determinedCareDetails

  if (isFacility) {
    return {
      isFacility,
      shouldCost: facilityAddOnShouldCost || facilityShouldCost,
      benefitAmount: facilityAddOnBenefitAmount || facilityBenefitAmount,
      sidecarCovers: facilityAddOnSidecarCovers || facilitySidecarCovers,
    }
  }

  return {
    isFacility,
    shouldCost: nonFacilityAddOnShouldCost || nonFacilityShouldCost,
    benefitAmount: nonFacilityAddOnBenefitAmount || nonFacilityBenefitAmount,
    sidecarCovers: nonFacilityAddOnSidecarCovers || nonFacilitySidecarCovers,
  }
}

/**
 * Return the address object of the member being searched as.
 *
 * @param {array} members All the members on the account
 * @param {string} searchingAsUuid The uuid of the member being searched as
 */
export const getSearchingAsPolicyAddress = (members, searchingAsUuid) => {
  // find the member we are "searching as"
  const memberSearchingAs = members.find(
    member => member.uuid === searchingAsUuid,
  )
  // the address of "searching as" (from their policy)
  const policyAddress = pathOr({}, ['policy', 'address'], memberSearchingAs)
  return policyAddress
}

/**
 * Get the icon name of a care service based on the "type" and "category" that
 * the BE sends us. If the type is APC or ASC, we'll use the hospital icon. If
 * no categorySlug is provided, we'll default to the "doctor" icon. Beyond that,
 * we'll use the categorySlug.
 *
 * @param {object} item The object the BE sends us that should include a type
 *   and categorySlug
 * @returns {string} String that can be passed into an Icon or Thumbnail
 *   component
 */
export const getIconName = item => {
  return /(ASC|APC)/.test(item.type)
    ? 'hospital'
    : getCareCategoryIcon(item.categorySlug)
}
